import {
  add,
  addMinutes,
  differenceInYears,
  format,
  intervalToDuration,
  isToday,
  isTomorrow,
  parseISO,
  subMinutes,
} from "date-fns";
import type { Duration } from 'date-fns'
import { formatInTimeZone, toZonedTime, fromZonedTime, format as tzFormat } from "date-fns-tz";

export type DateLike = Date | string;
export enum TimeFormatPreference {
  h12 = "standard",
  h24 = "military",
}

export const castDate = (date: DateLike) => {
  if (typeof date === "string") {
    return parseISO(date);
  } else {
    return date;
  }
};

export const castDateToString = (date: DateLike) => {
  if (typeof date === "string") {
    return parseISO(date).toISOString();
  } else {
    return date.toISOString();
  }
};

type OptionalTz = string | null | undefined;

/**
 * Core formatting function with timezone support
 */
export const datef = (
  date: DateLike,
  formatStr: string,
  tz?: string | null | undefined,
  timeFormatPreference?: TimeFormatPreference
) => {
  if (timeFormatPreference === TimeFormatPreference.h24) {
    formatStr = formatStr.replace(/hh:mm\s+a/g, 'HHmm');
    formatStr = formatStr.replace(/h:mma/g, 'HHmm');
    formatStr = formatStr.replace(/(?<!h)h:mm(?!\s*a)/g, 'HHmm');
  }
  return tz ? formatInTimeZone(date, tz, formatStr) : format(date, formatStr);
} 

/**
 * Get the appropriate time format string based on preferences
 * Returns date-fns format tokens only
 */
export const getTimeFormat = (options: {
  timeDisplay?: TimeFormatPreference;
  ampm?: boolean;
  showTimezone?: boolean;
} = {}): string => {
  const { 
    timeDisplay = TimeFormatPreference.h12, 
    ampm = true, 
    showTimezone = false 
  } = options;
  
  const isMilitary = timeDisplay === TimeFormatPreference.h24;
  
  if (showTimezone) {
    return isMilitary ? "HHmm z" : (ampm ? "h:mma zz" : "h:mm zzz");
  }
  
  return isMilitary ? "HHmm" : (ampm ? "h:mma" : "h:mm");
};

/**
 * Core time formatting functions - single source of truth
 */
export const timeLabel = (
  date: DateLike, 
  options: {
    ampm?: boolean;
    timezone?: OptionalTz;
    timeFormat?: TimeFormatPreference;
  } = {}
) => {
  const { ampm = true, timezone, timeFormat = TimeFormatPreference.h12 } = options;
  const formatStr = getTimeFormat({ timeDisplay: timeFormat, ampm, showTimezone: false });
  return datef(date, formatStr, timezone, timeFormat);
};

export const timeLabelWithZone = (
  date: DateLike,
  options: {
    ampm?: boolean;
    timezone?: OptionalTz;
    timeFormat?: TimeFormatPreference;
  } = {}
) => {
  const { ampm = true, timezone, timeFormat = TimeFormatPreference.h12 } = options;
  const formatStr = getTimeFormat({ timeDisplay: timeFormat, ampm, showTimezone: true });
  return datef(date, formatStr, timezone, timeFormat);
};

export const timeRangeLabel = (
  startTime: DateLike,
  endTime: DateLike,
  options: {
    showDate?: boolean;
    showTimeZone?: boolean;
    timezone?: OptionalTz;
    timeFormat?: TimeFormatPreference;
  } = {}
) => {
  const { showDate = false, showTimeZone = false, timezone, timeFormat = TimeFormatPreference.h12 } = options;
  
  const startFormat = showDate 
    ? `EEEE MMM dd, ${getTimeFormat({ timeDisplay: timeFormat, ampm: false, showTimezone: false })}`
    : getTimeFormat({ timeDisplay: timeFormat, ampm: false, showTimezone: false });
  const endFormat = showTimeZone 
    ? getTimeFormat({ timeDisplay: timeFormat, ampm: true, showTimezone: true })
    : getTimeFormat({ timeDisplay: timeFormat, ampm: true, showTimezone: false });
  
  return [
    datef(startTime, startFormat, timezone, timeFormat),
    datef(endTime, endFormat, timezone, timeFormat),
  ].join("-");
};

export const timeMarkLabel = (
  date: DateLike, 
  options: {
    timezone?: OptionalTz;
    timeFormat?: TimeFormatPreference;
  } = {}
) => {
  const { timezone, timeFormat = TimeFormatPreference.h12 } = options;
  const formatStr = timeFormat === TimeFormatPreference.h24 ? "HHmm" : "haaa";
  return datef(date, formatStr, timezone, timeFormat);
};

export const dateTimeLabel = (
  dateTime: DateLike | undefined,
  options: {
    timezone?: OptionalTz;
    timeFormat?: TimeFormatPreference;
  } = {}
) => {
  const { timezone, timeFormat = TimeFormatPreference.h12 } = options;
  if (dateTime === undefined) return "";
  
  const timeFormatStr = getTimeFormat({ timeDisplay: timeFormat, ampm: true, showTimezone: false });
  return datef(dateTime, `yyyy-MM-dd ${timeFormatStr}`, timezone, timeFormat);
};

// Legacy functions for backward compatibility (will be deprecated)
export const dbDate = (date: DateLike | undefined | null, tz?: OptionalTz) => {
  return date ? datef(castDate(date), "yyyy-MM-dd", tz, TimeFormatPreference.h12) : "";
};

export const parseDbDate = castDate;

export const roundToNearestInterval = (
  date: DateLike,
  intervalMins: number
): Date => {
  date = castDate(date);
  const minutes = date.getMinutes();
  const remainder = minutes % intervalMins;
  const roundedMinutes =
    remainder < intervalMins / 2.0
      ? minutes - remainder
      : minutes + (intervalMins - remainder);
  return new Date(date.setMinutes(roundedMinutes, 0, 0));
};

/**
 * Rounds a date to the nearest interval in the specified timezone
 * @param date The date to round
 * @param intervalMins The interval in minutes (e.g., 15)
 * @param timezone The timezone to perform rounding in (e.g., "America/Los_Angeles")
 * @returns The rounded date in UTC
 */
export const roundToNearestIntervalInTimezone = (
  date: DateLike,
  intervalMins: number,
  timezone?: string | null
): Date => {
  // If no timezone provided, fall back to local rounding
  if (!timezone) {
    return roundToNearestInterval(date, intervalMins);
  }

  // Convert to timezone for rounding
  const zonedDate = toZonedTime(castDate(date), timezone);
  
  // Round in the target timezone
  const minutes = zonedDate.getMinutes();
  const roundedMinutes = Math.round(minutes / intervalMins) * intervalMins;
  
  // Create rounded date in the target timezone
  const roundedZonedDate = new Date(zonedDate);
  roundedZonedDate.setMinutes(roundedMinutes, 0, 0);
  
  // Convert back to UTC using fromZonedTime
  return fromZonedTime(roundedZonedDate, timezone);
};

export const formatFullDate = (date: DateLike, tz?: OptionalTz) =>
  datef(date, "MMMM d, yyyy", tz);

export const formatDateWithDayOfWeek = (date: DateLike, tz?: OptionalTz) =>
  datef(date, "EEEE, MMMM d", tz);

export const formatFullDateWithDayOfWeek = (date: DateLike, tz?: OptionalTz) =>
  datef(date, "EEEE, MMM d yyyy", tz);

export const formatShortDate = (date: DateLike, tz?: OptionalTz) =>
  datef(castDate(date), "M/d/yyyy", tz || 'UTC');

export const timezoneAbbreviation = (timezone: string, _?: OptionalTz) => {
  const date = new Date();
  let zonedDate = date;
  if (timezone) {
    zonedDate = toZonedTime(date, timezone);
  }
  const abbreviation = datef(zonedDate, "zzz", timezone);
  return abbreviation;
};

export const formatDateHeader = (date: Date | string) => {
  const adjustedDate =
    typeof date === "string" ? new Date(date + "T00:00:00") : date;

  if (isToday(adjustedDate)) {
    return format(adjustedDate, "'Today, 'MMM d");
  } else if (isTomorrow(adjustedDate)) {
    return format(adjustedDate, "'Tomorrow, 'MMM d");
  }

  return format(adjustedDate, "EEE, MMM d");
};

export const patientAge = (date: DateLike, now: Date = new Date()) => {
  return differenceInYears(now, castDate(date));
};

/**
 * Date formatter factory - creates bound formatters with timezone and time format preferences
 */
export const createDateFormatter = (
  timezone?: string | null, 
  timeFormatPreference: TimeFormatPreference = TimeFormatPreference.h12,
  toggleTimeFormat?: () => void
) => ({
  timezone,
  timeFormatPreference,
  toggleTimeFormat,
  
  // Core formatting functions with preferences applied
  datef: (date: DateLike, formatStr: string) => datef(date, formatStr, timezone, timeFormatPreference),
  
  timeLabel: (date: DateLike, ampm = true, overrideTimezone?: OptionalTz) => 
    timeLabel(date, { ampm, timezone: overrideTimezone || timezone, timeFormat: timeFormatPreference }),
    
  timeLabelWithZone: (date: DateLike, ampm = true, overrideTimezone?: OptionalTz) => 
    timeLabelWithZone(date, { ampm, timezone: overrideTimezone || timezone, timeFormat: timeFormatPreference }),
    
  timeRangeLabel: (startTime: DateLike, endTime: DateLike, showDate = false, showTimeZone = false, overrideTimezone?: OptionalTz) => 
    timeRangeLabel(startTime, endTime, { showDate, showTimeZone, timezone: overrideTimezone || timezone, timeFormat: timeFormatPreference }),
    
  timeMarkLabel: (date: DateLike) => 
    timeMarkLabel(date, { timezone, timeFormat: timeFormatPreference }),
    
  dateTimeLabel: (dateTime: DateLike | undefined) => 
    dateTimeLabel(dateTime, { timezone, timeFormat: timeFormatPreference }),
    
  // Format strings for components
  getTimeFormat: (includePeriod?: boolean, includeTimezone?: boolean) => 
    getTimeFormat({ 
      timeDisplay: timeFormatPreference, 
      ampm: includePeriod, 
      showTimezone: includeTimezone 
    }),
    
  // Date functions (unchanged)
  dbDate: (date: DateLike | undefined | null) => dbDate(date, timezone),
  formatFullDate: (date: DateLike) => formatFullDate(date, timezone),
  formatDateWithDayOfWeek: (date: DateLike) => formatDateWithDayOfWeek(date, timezone),
  formatFullDateWithDayOfWeek: (date: DateLike) => formatFullDateWithDayOfWeek(date, timezone),
  formatShortDate: (date: DateLike) => formatShortDate(date, timezone),
  timezoneAbbreviation: (tz: string) => timezoneAbbreviation(tz, timezone),
});

export const getFriendlyTimezoneName = (timezone: string) => {
  return tzFormat(new Date(), 'zzzz', { timeZone: timezone }).toString().replace(/(Standard|Daylight)/g, '').replace(/\s+/g, ' ').trim();
};

/**
 * Short timezone label for display next to times. Intl has no short
 * abbreviation for some zones (e.g. Asia/Dubai) and falls back to a
 * "GMT+4"-style offset; use the friendly zone name in that case.
 */
export const timezoneDisplayLabel = (timezone: string) => {
  const abbreviation = timezoneAbbreviation(timezone);
  return abbreviation.startsWith("GMT") ? getFriendlyTimezoneName(timezone) : abbreviation;
};

export const addDurations = (...durations: Duration[]) => {
  const start = new Date(0);
  const end = durations.reduce(add, start)
  return intervalToDuration({ start, end })
}

export const toLocalTimezone = (d: Date) => addMinutes(d, d.getTimezoneOffset());
export const fromLocalTimezone = (d: Date) => subMinutes(d, d.getTimezoneOffset());

export const calendarDayToTimestamps = (calendarDay: string, timezone: string) => {
  const startUtc = fromZonedTime(`${calendarDay}T00:00:00.000`, timezone);
  const endUtc = fromZonedTime(`${calendarDay}T23:59:59.999`, timezone);

  return {
    startUtc,
    endUtc,
  };
};

export function roundTime15(date: Date): Date {
  const minutes = 15;
  const offset = date.getMinutes() % minutes;
  const round = date.getSeconds() >= 30 ? 1 : 0;

  const newDate = new Date(date);
  if (offset + round > minutes / 2) {
      newDate.setMinutes(newDate.getMinutes() + (minutes - offset));
  } else {
      newDate.setMinutes(newDate.getMinutes() - offset);
  }

  newDate.setSeconds(0);
  newDate.setMilliseconds(0);

  return newDate;
}

export function formatInterval(interval: Duration) {
  return (interval.hours ? `${interval.hours}hrs` : '') + (interval.minutes ? ` ${interval.minutes}min` : '');
}

export const dateLabelWithMaybeYear = (date: Date) => {
  const isToday = date.toDateString() === new Date().toDateString();
  const isSameYear = date.getFullYear() === new Date().getFullYear();
  const formatter = [isToday ? "'Today'" : "EEE", "MMM d", isSameYear ? null : "yyyy"].filter((x) => !!x).join(", ");
  return datef(date, formatter);
};

export const getPatientDayStartUtc = (utcDate: Date, timezone: string) => {
  const zonedTime = toZonedTime(utcDate, timezone); // convert to patient wall time
  zonedTime.setHours(0, 0, 0, 0); // set to start of day
  return fromZonedTime(zonedTime, timezone); // back to UTC
};

// TODO: also defined in last PR, can consolidate
export const addParsedTimeToPatientDayStart = (
  patientDayStartUtc: Date,
  timeValue: string
): Date => {
  const [hoursString = "0", minutesString = "0"] = timeValue.split(":");
  const hours = Number.parseInt(hoursString, 10) || 0;
  const minutes = Number.parseInt(minutesString, 10) || 0;
  return new Date(patientDayStartUtc.getTime() + (hours * 60 + minutes) * 60000);
};

export const getUtcEquivalentOfPatientWallTime = (utcTime: Date, patientWallTime: string, timezone: string) => {
  const patientDayStart = getPatientDayStartUtc(utcTime, timezone);

  return addParsedTimeToPatientDayStart(patientDayStart, patientWallTime);
};
